import { IChannel, ChannelType, IChannelUser, IChannelResponse } from '../../../common/types/channels.types'
import { useEffect, useState } from 'react'
import { useChannelsApi } from '../../../hooks/use-channels-api/useChannelsApi'
import { BackendPartner, BackendUser } from 'common/types'
import { useChannel } from './useChannel'
import { usePartnerProfile } from 'pages/profile/hooks/usePartnerProfile'
import { useAuth } from 'pages/auth/hooks/useAuth'

export const useChannels = () => {
  const { createChannel, getChannels, getPartnerChannelsById } = useChannelsApi()
  const { getChannelUserType } = useChannel()
  const { company } = usePartnerProfile()
  const { backendUser } = useAuth()
  const [channels, setChannels] = useState<IChannel[]>([])
  const [channelsJoinedByUser, setChannelsJoinedByUser] = useState<IChannel[]>([])
  const [channelsJoinedResponse, setChannelsJoinedResponse] = useState<IChannelResponse>()
  const [page, setPage] = useState<number>(1)

  const createDefaultChannel = async (backendUser: BackendUser, backendPartner: BackendPartner) => {
    await createChannel(backendUser?.id, {
      partnerId: backendPartner?.id,
      description: "",
      name: backendPartner?.name,
      type: ChannelType.PRIVATE,
      tags: [],
      metaData: null
    })
  }

  const getPartnerChannels = async (backendPartner: BackendPartner) => {
    if (backendPartner) {
      const response = await getPartnerChannelsById(backendPartner?.id)

      if (response) {
        setChannels(response)
        return response
      }
    }
  }

  const getChannelsJoinedByUser = async () => {
    if (backendUser) {
      const response: IChannelResponse = await getChannels(backendUser?.id, 5, page)
      if (response) {
        setChannelsJoinedByUser(response?.data)
        setChannelsJoinedResponse(response)
      }
    }
  }

  const fetchMoreChannelsJoinedByUser = async () => {
    if (backendUser) {
      const response = await getChannels(backendUser?.id, 5, page)
      if (response) {
        setChannelsJoinedByUser([...channelsJoinedByUser, ...response?.data])
      }
    }
  }

  const getMoreChannelsJoinedByUser = async () => {
    setPage(page + 1)
  }

  useEffect(() => {
    if (page > 1) {
      fetchMoreChannelsJoinedByUser()
    } else {
      getChannelsJoinedByUser()
    }
  }, [page])

  useEffect(() => {
    const channels = async () => {
      await getPartnerChannels(company)
    }
    channels()
  }, [company?.id])

  const getAllChannels = async (userId: string, updateChannels = true) => {
    const response: IChannelResponse = await getChannels(userId)

    if (response) {
      const newChannels = await Promise.all(
        response.data.map(async channel => {
          const user = (await getChannelUserType(channel.id, userId)) as IChannelUser
          return {
            ...channel,
            isAdmin: user?.isAdmin
          }
        })
      )

      setChannels(
        newChannels?.sort((a, b) => {
          return a.isAdmin ? -1 : 1
        })
      )

      return response
    }
  }

  return {
    createDefaultChannel,
    getPartnerChannels,
    getAllChannels,
    setChannels,
    channels,
    channelsJoinedByUser,
    channelsJoinedResponse,
    getMoreChannelsJoinedByUser
  } as const
}
